import { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, type Artikel } from '../lib/api';

// Artikelstamm (Einkauf): alle Katalogartikel (z. B. Würth-Liste) durchsuchen, nach Warengruppe filtern,
// nach „meistbestellt" (Zugriffe) sortieren. Pflege (Warengruppe / aktiv) nur Admin oder Meister.

const SEITE = 50;
const OHNE = '__ohne__';
const zahl = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('de-DE'));
function fmtDatum(v: string | null | undefined): string {
  const m = v ? /^(\d{4})-(\d{2})-(\d{2})/.exec(v) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '—';
}

export function Artikelstamm() {
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [wg, setWg] = useState('');
  const [sort, setSort] = useState<'zugriffe' | 'name'>('zugriffe');
  const [inaktiv, setInaktiv] = useState(false);
  const [offset, setOffset] = useState(0);
  const [edit, setEdit] = useState<Artikel | null>(null);
  const [fehler, setFehler] = useState('');
  const me = useApi(() => api.dashboard.me(), []);
  const darfPflegen = me.data?.user?.role === 'admin' || me.data?.user?.role === 'meister';

  useEffect(() => { const t = setTimeout(() => { setQLive(q.trim()); setOffset(0); }, 250); return () => clearTimeout(t); }, [q]);
  const aktiv = inaktiv ? '0' : '1';
  const gruppen = useApi(() => api.artikel.gruppen(aktiv), [aktiv]);
  const liste = useApi(() => api.artikel.list({ q: qLive, wg: wg || undefined, sort, aktiv, limit: SEITE, offset }), [qLive, wg, sort, aktiv, offset]);
  const gesamt = (gruppen.data?.data ?? []).reduce((s, g) => s + g.n, 0);
  const total = liste.data?.total ?? 0;

  async function speichern() {
    if (!edit) return;
    setFehler('');
    try {
      await api.artikel.update(edit.id, { warengruppe: (edit.warengruppe ?? '').trim() || null, aktiv: edit.aktiv ? 1 : 0 });
      setEdit(null); liste.reload(); gruppen.reload();
    } catch (e) { setFehler((e as Error).message); }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">Einkauf</div>
        <h1 className="text-2xl font-bold text-slate-900">📚 Artikelstamm</h1>
        <p className="text-sm text-slate-600">Katalogartikel (z. B. Würth) — suchen, nach Warengruppe filtern, „meistbestellt" zuerst. In der BANF werden Positionen aus diesem Stamm gewählt.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input className="input w-72" placeholder="🔍 Bezeichnung, Art.-Nr., z. B. „M8 DIN933“ …" aria-label="Artikel suchen"
               value={q} onChange={e => setQ(e.target.value)} />
        <select className="input" aria-label="Sortierung" value={sort} onChange={e => { setSort(e.target.value as 'zugriffe' | 'name'); setOffset(0); }}>
          <option value="zugriffe">Meistbestellt zuerst</option>
          <option value="name">A – Z</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={inaktiv} onChange={e => { setInaktiv(e.target.checked); setWg(''); setOffset(0); }} /> nur ausgeblendete zeigen
        </label>
        <span className="text-sm text-slate-500" data-testid="stamm-total">{total.toLocaleString('de-DE')} Artikel</span>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="stamm-gruppen">
        <button type="button" onClick={() => { setWg(''); setOffset(0); }}
                className={`px-2.5 py-1 rounded-full text-xs border ${wg === '' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
          Alle ({gesamt.toLocaleString('de-DE')})
        </button>
        {(gruppen.data?.data ?? []).map(g => (
          <button key={g.name} type="button" onClick={() => { setWg(g.name); setOffset(0); }}
                  className={`px-2.5 py-1 rounded-full text-xs border ${wg === g.name ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
            {g.name === OHNE ? 'Ohne Warengruppe' : g.name} ({g.n})
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[860px]">
          <thead className="bg-slate-100 border-b-2 border-slate-300 text-xs text-slate-600 uppercase">
            <tr>{['Art.-Nr.', 'Bezeichnung', 'Warengruppe', 'Lieferant', 'Bestellt (×)', 'Menge'].map(h => <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {liste.loading && <tr><td colSpan={6} className="text-center py-10 text-slate-500">Lädt …</td></tr>}
            {liste.data && liste.data.data.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500">Keine Artikel</td></tr>}
            {liste.data?.data.map(a => (
              <tr key={a.id} className={darfPflegen ? 'cursor-pointer hover:bg-slate-50' : ''} onClick={() => darfPflegen && setEdit({ ...a })}>
                <td className="px-2.5 py-1.5 font-mono text-xs whitespace-nowrap">{a.lieferanten_artikelnr ?? '—'}</td>
                <td className="px-2.5 py-1.5">{a.bezeichnung}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{a.warengruppe ?? <span className="text-slate-400">—</span>}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{a.lieferant ?? '—'}</td>
                <td className="px-2.5 py-1.5 text-right font-semibold" title={a.stat_von ? `Zeitraum ${fmtDatum(a.stat_von)} – ${fmtDatum(a.stat_bis)}` : ''}>{zahl(a.zugriffe)}</td>
                <td className="px-2.5 py-1.5 text-right">{zahl(a.gesamtmenge)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > SEITE && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm text-slate-600">
            <span>{offset + 1}–{Math.min(offset + SEITE, total)} von {total.toLocaleString('de-DE')}</span>
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - SEITE))}>← Zurück</button>
              <button className="btn-secondary" disabled={offset + SEITE >= total} onClick={() => setOffset(offset + SEITE)}>Weiter →</button>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">„Bestellt (×)" = wie oft der Artikel im Zeitraum der Lieferanten-Statistik bestellt wurde (Würth: 01.10.2023 – 07.10.2026). Mit der Maus über die Zahl fahren zeigt den Zeitraum.</p>

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-12" onClick={e => e.stopPropagation()} data-testid="stamm-edit">
            <div className="px-6 py-4 border-b border-slate-200 font-semibold">Artikel pflegen</div>
            <div className="p-6 space-y-3 text-sm">
              <div><div className="font-medium">{edit.bezeichnung}</div><div className="text-slate-500">{edit.lieferant ?? '—'} · Art.-Nr. {edit.lieferanten_artikelnr ?? '—'}</div></div>
              <label className="block">Warengruppe
                <input className="input w-full mt-1" list="wg-liste" value={edit.warengruppe ?? ''} onChange={e => setEdit({ ...edit, warengruppe: e.target.value })} />
                <datalist id="wg-liste">{(gruppen.data?.data ?? []).filter(g => g.name !== OHNE).map(g => <option key={g.name} value={g.name} />)}</datalist>
              </label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!edit.aktiv} onChange={e => setEdit({ ...edit, aktiv: e.target.checked ? 1 : 0 })} /> aktiv (in Suche und BANF sichtbar)</label>
              {fehler && <div className="text-red-700">{fehler}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary" onClick={() => setEdit(null)}>Abbrechen</button>
                <button className="bg-brand text-white rounded-lg px-4 py-2 font-medium hover:opacity-90" onClick={speichern}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
